import { collection, query, where, getDocs, doc, getDoc, addDoc, updateDoc, deleteDoc, Timestamp, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import zoomService from './zoomService';

const CLASSES_COLLECTION = 'zoom_classes';

/**
 * 학생의 수업 목록 조회
 * @param {string} studentId - 학생 ID
 * @param {string} status - 수업 상태 필터 (optional: 'scheduled', 'ongoing', 'completed', 'cancelled')
 * @returns {Promise<Array>} 수업 목록
 */
export const getStudentClasses = async (studentId, status = null) => {
  try {
    let q = query(
      collection(db, CLASSES_COLLECTION),
      where('studentId', '==', studentId),
      orderBy('scheduledStartTime', 'desc')
    );

    if (status) {
      q = query(
        collection(db, CLASSES_COLLECTION),
        where('studentId', '==', studentId),
        where('status', '==', status),
        orderBy('scheduledStartTime', 'desc')
      );
    }

    const querySnapshot = await getDocs(q);
    const classes = [];

    querySnapshot.forEach((doc) => {
      classes.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return classes;
  } catch (error) {
    console.error('Error fetching student classes:', error);
    throw new Error('수업 목록을 불러오는데 실패했습니다.');
  }
};

/**
 * 모든 수업 목록 조회 (날짜 필터링)
 * @param {string} status - 수업 상태 필터 (optional)
 * @returns {Promise<Array>} 수업 목록
 */
export const getAllClasses = async (status = null) => {
  try {
    let q;

    if (status) {
      q = query(
        collection(db, CLASSES_COLLECTION),
        where('status', '==', status),
        orderBy('scheduledStartTime', 'desc')
      );
    } else {
      q = query(
        collection(db, CLASSES_COLLECTION),
        orderBy('scheduledStartTime', 'desc')
      );
    }

    const querySnapshot = await getDocs(q);
    const classes = [];

    querySnapshot.forEach((doc) => {
      classes.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return classes;
  } catch (error) {
    console.error('Error fetching all classes:', error);
    throw new Error('수업 목록을 불러오는데 실패했습니다.');
  }
};

/**
 * 수업 생성 (Zoom 미팅 생성 + Firestore 저장)
 * @param {string} studentId - 학생 ID
 * @param {string} teacherId - 교사 ID
 * @param {Object} classData - 수업 정보
 * @param {string} classData.title - 제목
 * @param {string} classData.description - 설명
 * @param {Date} classData.scheduledStartTime - 시작 시간
 * @param {number} classData.duration - 지속 시간 (분)
 * @returns {Promise<Object>} 생성된 수업 정보
 */
export const createClass = async (studentId, teacherId, classData) => {
  try {
    // 1. Zoom 미팅 생성
    const zoomMeeting = await zoomService.createMeeting({
      title: classData.title,
      description: classData.description,
      startTime: classData.scheduledStartTime.toISOString(),
      duration: classData.duration
    });

    // 2. Firestore에 수업 정보 저장
    const scheduledEndTime = new Date(classData.scheduledStartTime);
    scheduledEndTime.setMinutes(scheduledEndTime.getMinutes() + classData.duration);

    const classDoc = {
      studentId,
      teacherId,

      // Zoom 정보
      zoomMeetingId: String(zoomMeeting.meetingId),
      zoomJoinUrl: zoomMeeting.joinUrl,
      zoomStartUrl: zoomMeeting.startUrl,
      zoomPassword: zoomMeeting.password || '',

      // 수업 정보
      title: classData.title,
      description: classData.description || '',
      scheduledStartTime: Timestamp.fromDate(classData.scheduledStartTime),
      scheduledEndTime: Timestamp.fromDate(scheduledEndTime),
      duration: classData.duration,

      // 상태
      status: 'scheduled',

      // 참여자 정보 (완료 후 업데이트)
      participantCount: 0,
      participants: [],

      // 녹화본 (완료 후 업데이트)
      recordingUrl: null,
      recordingPassword: null,

      // 메타데이터
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      createdBy: teacherId
    };

    const docRef = await addDoc(collection(db, CLASSES_COLLECTION), classDoc);

    return {
      id: docRef.id,
      ...classDoc
    };
  } catch (error) {
    console.error('Error creating class:', error);
    throw new Error(error.message || '수업 생성에 실패했습니다.');
  }
};

/**
 * 수업 정보 수정
 * @param {string} classId - 수업 ID
 * @param {Object} updates - 수정할 정보
 * @returns {Promise<void>}
 */
export const updateClass = async (classId, updates) => {
  try {
    const classRef = doc(db, CLASSES_COLLECTION, classId);

    await updateDoc(classRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating class:', error);
    throw new Error('수업 정보 수정에 실패했습니다.');
  }
};

/**
 * 수업 삭제 (Zoom 미팅도 함께 삭제)
 * @param {string} classId - 수업 ID
 * @returns {Promise<void>}
 */
export const deleteClass = async (classId) => {
  try {
    // 1. Firestore에서 수업 정보 조회
    const classDoc = await getClassDetails(classId);

    // 2. Zoom 미팅 삭제 시도 (실패해도 Firestore는 삭제)
    try {
      await zoomService.deleteMeeting(classDoc.zoomMeetingId);
    } catch (error) {
      console.warn('Zoom 미팅 삭제 실패 (이미 삭제되었거나 만료됨):', error);
    }

    // 3. Firestore에서 수업 삭제
    await deleteDoc(doc(db, CLASSES_COLLECTION, classId));
  } catch (error) {
    console.error('Error deleting class:', error);
    throw new Error('수업 삭제에 실패했습니다.');
  }
};

/**
 * 수업 상세 정보 조회
 * @param {string} classId - 수업 ID
 * @returns {Promise<Object>} 수업 정보
 */
export const getClassDetails = async (classId) => {
  try {
    const classRef = doc(db, CLASSES_COLLECTION, classId);
    const classDoc = await getDoc(classRef);

    if (!classDoc.exists()) {
      throw new Error('수업을 찾을 수 없습니다.');
    }

    return {
      id: classDoc.id,
      ...classDoc.data()
    };
  } catch (error) {
    console.error('Error fetching class details:', error);
    throw new Error(error.message || '수업 정보 조회에 실패했습니다.');
  }
};

/**
 * 수업 완료 처리 및 데이터 동기화
 * @param {string} classId - 수업 ID
 * @returns {Promise<void>}
 */
export const completeClass = async (classId) => {
  try {
    const classData = await getClassDetails(classId);

    // 1. Zoom API로 참여자 정보 조회
    let participants = [];
    let participantCount = 0;

    try {
      participants = await zoomService.getMeetingParticipants(classData.zoomMeetingId);
      participantCount = participants.length;
    } catch (error) {
      console.warn('참여자 정보 조회 실패:', error);
      // 참여자 정보 조회 실패해도 수업은 완료 처리
    }

    // 2. 녹화본 정보 조회
    let recordingUrl = null;
    let recordingPassword = null;

    try {
      const recording = await zoomService.getMeetingRecordings(classData.zoomMeetingId);
      if (recording) {
        recordingUrl = recording.url;
        recordingPassword = recording.password;
      }
    } catch (error) {
      console.warn('녹화본 정보 조회 실패:', error);
      // 녹화본 조회 실패해도 수업은 완료 처리
    }

    // 3. Firestore 업데이트
    await updateClass(classId, {
      status: 'completed',
      actualEndTime: Timestamp.now(),
      participantCount,
      participants: participants.map(p => ({
        name: p.name,
        email: p.email,
        joinTime: p.joinTime ? Timestamp.fromDate(new Date(p.joinTime)) : null,
        leaveTime: p.leaveTime ? Timestamp.fromDate(new Date(p.leaveTime)) : null,
        duration: p.duration
      })),
      recordingUrl,
      recordingPassword
    });

    console.log('수업 완료 처리 성공:', {
      classId,
      participantCount,
      hasRecording: !!recordingUrl
    });
  } catch (error) {
    console.error('Error completing class:', error);
    throw new Error(error.message || '수업 완료 처리에 실패했습니다.');
  }
};

/**
 * 수업 상태 업데이트 (scheduled → ongoing)
 * @param {string} classId - 수업 ID
 * @returns {Promise<void>}
 */
export const startClass = async (classId) => {
  try {
    await updateClass(classId, {
      status: 'ongoing',
      actualStartTime: Timestamp.now()
    });
  } catch (error) {
    console.error('Error starting class:', error);
    throw new Error('수업 시작 처리에 실패했습니다.');
  }
};

export const classService = {
  getStudentClasses,
  getAllClasses,
  createClass,
  updateClass,
  deleteClass,
  getClassDetails,
  completeClass,
  startClass
};

export default classService;
